import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ListingTypeTable from '../../../components/tables/ListingTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useListingTypeSearch, useDeleteListingType } from '../../../hooks/useListingTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ListingTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const ListingTypeList: React.FC = () => {
  const { search } = useListingTypeSearch();
  const deleteMutation = useDeleteListingType();
  const [items, setItems] = useState<ListingTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Listing Types', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      setIsLoading(true);
      const { results, totalCount } = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(results);
      setTotalRecords(totalCount);
      return totalCount;
    } catch (error) {
      console.error('Error fetching Listing Types:', error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: ListingTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting ListingType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/listingtypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.ListingType.View"
    >
      <Card
        title="Manage Listing Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/listingtypes/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ListingTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/listingtypes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ListingTypeList;
